import { Component, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { Icons, SelectOption } from '@agilox/ui-common';
import { DatepickerTranslationService } from '@agilox/common';

@Component({
	selector: 'ui-datepicker-navigation',
	templateUrl: './datepicker-navigation.component.html',
	standalone: false,
})
export class DatepickerNavigationComponent implements OnInit {
	private datepickerTranslationService = inject(DatepickerTranslationService);

	currentlyDisplayMonth = input.required<Date>();

	minDate = input<Date | undefined>(undefined);
	maxDate = input<Date | undefined>(undefined);
	disabled = input<boolean>(false);

	dateChange = output<Date>();
	todayClick = output<void>();

	previousMonthDisabled = signal(false);
	nextMonthDisabled = signal(false);

	yearSelectOptions: SelectOption<number>[] = [];
	monthSelectOptions: SelectOption<number>[] = this.generateMonthOptions();

	month = signal<number | null>(null);
	year = signal<number | null>(null);

	constructor() {
		effect(() => {
			if (this.currentlyDisplayMonth()) {
				this.setFormValues(this.currentlyDisplayMonth());
				this.updateNavigation();
			}
		});
	}

	private setFormValues(date: Date) {
		// Check if date is not already set
		if (this.month() !== date.getMonth() + 1 || this.year() !== date.getFullYear()) {
			this.month.set(date.getMonth() + 1);
			this.year.set(date.getFullYear());
		}
	}

	onMonthChange(month: number): void {
		this.month.set(month);
		this.emitDate();
	}

	onYearChange(year: number): void {
		this.year.set(year);
		this.emitDate();
	}

	private emitDate(): void {
		const month = this.month();
		const year = this.year();
		if (month && year) {
			this.dateChange.emit(new Date(year, month - 1));
			this.updateNavigation();
		}
	}

	ngOnInit() {
		const minDate = this.minDate();
		const maxDate = this.maxDate();

		this.yearSelectOptions = this.generateYearOptions(
			minDate ? minDate.getFullYear() : 1900,
			maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 50
		);
	}

	goToPreviousMonth() {
		this.shiftMonth(-1);
	}

	goToNextMonth() {
		this.shiftMonth(1);
	}

	private shiftMonth(offset: number): void {
		const current = this.currentlyDisplayMonth();
		const date = new Date(current.getFullYear(), current.getMonth() + offset);
		this.setFormValues(date);
		this.emitDate();
	}

	updateNavigation() {
		this.previousMonthDisabled.set(false);
		this.nextMonthDisabled.set(false);

		const date: Date = this.currentlyDisplayMonth();
		const currentYear: number = date.getFullYear();
		const currentMonth: number = date.getMonth() + 1;

		const minDate = this.minDate();
		const maxDate = this.maxDate();
		const minYear: number | null = minDate ? minDate.getFullYear() : null;

		// Check if the current month is the minDate
		if (minDate && currentYear === minYear && currentMonth === minDate.getMonth() + 1) {
			this.previousMonthDisabled.set(true);
		}

		// Check if the current month is the maxDate
		if (
			maxDate &&
			currentYear === maxDate.getFullYear() &&
			currentMonth === maxDate.getMonth() + 1
		) {
			this.nextMonthDisabled.set(true);
		}
	}

	private generateYearOptions(minYear: number, maxYear: number): SelectOption<number>[] {
		const yearOptions: SelectOption<number>[] = [];
		for (let i = minYear; i <= maxYear; i++) {
			yearOptions.push({ value: i, title: i.toString() });
		}
		return yearOptions.reverse();
	}

	private generateMonthOptions(): SelectOption<number>[] {
		return Array(12)
			.fill(0)
			.map((_, index) => {
				return {
					value: index + 1,
					title: this.datepickerTranslationService.translate(
						'datepicker.months.' + index.toString()
					),
				};
			});
	}

	protected readonly Icons = Icons;
}
